import React, { createContext, useState, useEffect, useContext } from 'react';
import { apiFetch } from '../config/api';
import { useAuth } from './AuthContext';
import { debugError } from '../utils/debugLog';

// Create context
const FeatureFlagContext = createContext();

// Dynamic feature codes and modules - loaded from tenant_mgmt database via API
// These will be populated when features are fetched from the API
export let FEATURE_CODES = {
  // Developer Features (always available, not from database)
  DEVELOPER: 'DEVELOPER',
  ADMIN: 'ADMIN',
};

// Module definitions for hierarchical structure - loaded from tenant_mgmt database
export let FEATURE_MODULES = {};

// Provider component
export function FeatureFlagProvider({ children }) {
  const [features, setFeatures] = useState({});
  const [featureCodes, setFeatureCodes] = useState(FEATURE_CODES);
  const [featureModules, setFeatureModules] = useState(FEATURE_MODULES);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, tenant } = useAuth();
  const [apiResponseReceived, setApiResponseReceived] = useState(false);

  // Build feature codes and modules from API response
  function buildFeatureStructure(tenantFeatures) {
    if (!Array.isArray(tenantFeatures)) {
      return { codes: {}, modules: {} };
    }

    const codes = {
      DEVELOPER: 'DEVELOPER',
      ADMIN: 'ADMIN',
    };
    const modules = {};

    // Group features by module
    const moduleMap = new Map();

    tenantFeatures.forEach(feature => {
      if (!feature.feature_code) return;

      const featureCode = feature.feature_code.toUpperCase();
      codes[featureCode] = feature.feature_code;

      if (feature.module_id && feature.module_name) {
        // Generate module code from module name if not provided
        // Format: FLAG_{MODULE_NAME}_001 (matching the pattern used in database)
        const moduleCode =
          feature.module_code ||
          `FLAG_${feature.module_name.toUpperCase().replace(/\s+/g, '_')}_001`;

        if (!moduleMap.has(feature.module_id)) {
          moduleMap.set(feature.module_id, {
            module_id: feature.module_id,
            code: moduleCode,
            name: feature.module_name,
            description: feature.module_name, // Default description
            features: [],
          });
        }

        const module = moduleMap.get(feature.module_id);
        if (!module.features.includes(featureCode)) {
          module.features.push(featureCode);
        }
      }
    });

    // Convert module map to object
    moduleMap.forEach((module, moduleId) => {
      const moduleKey = module.name.toUpperCase().replace(/\s+/g, '_');
      modules[moduleKey] = module;
    });

    return { codes, modules };
  }

  // Fetch features function - extracted to be reusable
  const fetchFeatures = React.useCallback(async () => {
    // Prefer tenant_id from user if present, otherwise fall back to tenant object.
    // IMPORTANT: Multi-tenant safety
    // - We MUST NOT map aliases (e.g., 'demo') to hard-coded tenant_ids.
    // - If tenant_id is missing, treat as configuration/session error and do not guess.
    const tenantId = user?.tenant_id || tenant?.tenant_id;

    // Don't fetch if user is not loaded or tenant_id cannot be determined
    if (!user || !tenantId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Get tenant-specific features (includes module information)
      // tenant_id now comes strictly from user context (JWT / session)
      // Backend now returns ALL features for the tenant (no pagination needed)
      const apiUrl = `/api/v1/tenant/features`;

      let tenantFeaturesResponse;
      try {
        tenantFeaturesResponse = await apiFetch(apiUrl, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
          },
        });
      } catch (apiError) {
        debugError('[FeatureFlag] Failed to fetch features:', apiError.message);
        throw apiError;
      }

      // Build feature structure from API response
      let featureMap = {};
      let codes = { DEVELOPER: 'DEVELOPER', ADMIN: 'ADMIN' };
      let modules = {};

      if (
        tenantFeaturesResponse &&
        tenantFeaturesResponse.features &&
        Array.isArray(tenantFeaturesResponse.features)
      ) {
        // Build feature codes and modules from API data
        const structure = buildFeatureStructure(
          tenantFeaturesResponse.features
        );
        codes = { ...codes, ...structure.codes };
        modules = structure.modules;

        // Update exported constants (for backward compatibility)
        FEATURE_CODES = codes;
        FEATURE_MODULES = modules;

        // Process tenant-specific features - dynamically from database
        // IMPORTANT: All feature codes and enablement status come from the database
        // No hardcoded feature codes - everything is matched dynamically
        // Features not in the response are treated as disabled (not assigned to tenant)
        tenantFeaturesResponse.features.forEach(feature => {
          if (feature.feature_code) {
            // Use feature_code from database (case-insensitive matching)
            const featureCodeUpper = feature.feature_code.toUpperCase();

            // Get is_enabled status directly from database
            // Handle various boolean representations from API/database
            let isEnabled = false;
            if (feature.is_enabled === true) {
              isEnabled = true;
            } else if (feature.is_enabled === false) {
              isEnabled = false;
            } else if (
              feature.is_enabled === 'true' ||
              feature.is_enabled === 'True' ||
              feature.is_enabled === 'TRUE'
            ) {
              isEnabled = true;
            } else if (
              feature.is_enabled === 'false' ||
              feature.is_enabled === 'False' ||
              feature.is_enabled === 'FALSE'
            ) {
              isEnabled = false;
            } else if (feature.is_enabled === 1 || feature.is_enabled === '1') {
              isEnabled = true;
            } else if (feature.is_enabled === 0 || feature.is_enabled === '0') {
              isEnabled = false;
            } else if (
              feature.is_enabled === null ||
              feature.is_enabled === undefined
            ) {
              isEnabled = false;
            } else {
              isEnabled = Boolean(feature.is_enabled);
            }

            // Store feature enablement from database (no hardcoding)
            featureMap[featureCodeUpper] = isEnabled === true;
          }
        });

        // Important: Features that are not in the API response are NOT in the featureMap
        // This means they will be treated as disabled (undefined === false in isFeatureEnabled)
      }

      // Always enable developer features
      featureMap['DEVELOPER'] = true;

      if (process.env.NODE_ENV === 'development') {
        featureMap['ADMIN'] = true;
      }

      setFeatureCodes(codes);
      setFeatureModules(modules);
      setFeatures(featureMap);
      setApiResponseReceived(true);
    } catch (err) {
      debugError('[FeatureFlag] Error fetching features:', err.message);
      setError(err.message || 'Failed to load features');
      setFeatures({ DEVELOPER: true, ADMIN: true });
      setApiResponseReceived(false);
    } finally {
      setIsLoading(false);
    }
  }, [user, tenant]);

  // Fetch features when user is loaded (only on initial load or browser refresh)
  useEffect(() => {
    // Only fetch if user is available; fetchFeatures will resolve tenant_id
    if (!user) {
      return;
    }

    // Call fetchFeatures; it will derive tenantId from user/tenant context
    fetchFeatures().catch(err => {
      debugError('[FeatureFlag] Error in fetchFeatures:', err);
    });
  }, [user, fetchFeatures]);

  // Check if a feature is enabled (case-insensitive)
  // IMPORTANT: Feature enablement is determined dynamically from database
  // No hardcoded feature checks - all features come from tenant_features table
  const isFeatureEnabled = featureCode => {
    if (!featureCode) {
      return false;
    }

    // Normalize feature code to uppercase for case-insensitive comparison
    const normalizedCode = featureCode.toUpperCase();

    // Special case for developer features - always enabled (system feature, not from database)
    if (normalizedCode === 'DEVELOPER') {
      return true;
    }

    // During loading or before API response:
    // - Do NOT aggressively hide features, to avoid sidebar flicker
    // - Treat features as tentatively enabled until we know for sure
    //   (once API response arrives, real enable/disable state is applied)
    if (isLoading || !apiResponseReceived) {
      return true;
    }

    // Check feature map built from database response
    // If feature exists in map, return its value from database (true or false)
    // If feature doesn't exist in map, it means it's not in tenant_features table = disabled
    const featureValue = features[normalizedCode];
    if (featureValue !== undefined) {
      return featureValue === true; // Value comes directly from database is_enabled field
    }

    // Feature not in database response = not assigned to tenant = disabled
    return false;
  };

  // Check if a module is enabled (all features in module must be enabled)
  const isModuleEnabled = moduleCode => {
    const module = featureModules[moduleCode];
    if (!module) return false;

    return module.features.every(featureCode => isFeatureEnabled(featureCode));
  };

  // Get all features
  const getAllFeatures = () => {
    return { ...features };
  };

  // Get features by module
  const getFeaturesByModule = moduleCode => {
    const module = featureModules[moduleCode];
    if (!module) return {};

    const moduleFeatures = {};
    module.features.forEach(featureCode => {
      moduleFeatures[featureCode] = features[featureCode];
    });
    return moduleFeatures;
  };

  // Get all modules with their feature status
  const getAllModules = () => {
    const modules = {};
    Object.entries(featureModules).forEach(([key, module]) => {
      modules[key] = {
        ...module,
        isEnabled: isModuleEnabled(key),
        features: getFeaturesByModule(key),
      };
    });
    return modules;
  };

  // Refresh features function - can be called to reload from API
  const refreshFeatures = React.useCallback(async () => {
    await fetchFeatures();
  }, [fetchFeatures]);

  // Context value
  const value = {
    isFeatureEnabled,
    isModuleEnabled,
    isLoading,
    error,
    allFeatures: getAllFeatures(),
    allModules: getAllModules(),
    getFeaturesByModule,
    apiResponseReceived,
    FEATURE_CODES: featureCodes,
    FEATURE_MODULES: featureModules,
    refreshFeatures, // Expose refresh function
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

// Custom hook to use feature flags
export function useFeatureFlags() {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagProvider'
    );
  }
  return context;
}
